import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  FileText, 
  Download, 
  FolderOpen,
  Calendar,
  ChevronDown,
  ChevronRight,
  MapPin,
  Home,
  Building
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function SuburbResearch() {
  const [documents, setDocuments] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedLGAs, setExpandedLGAs] = useState({});
  const [expandedPropertyTypes, setExpandedPropertyTypes] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get("/research/documents");
      setDocuments(response.data.documents);
      setPropertyTypes(response.data.property_types || ["Houses", "Units/Townhouses"]);
      
      // Expand LGAs that have documents by default
      const expandedLGA = {};
      const expandedPT = {};
      Object.keys(response.data.documents).forEach(lga => {
        const lgaData = response.data.documents[lga];
        const hasDocuments = Object.values(lgaData).some(docs => docs.length > 0);
        if (hasDocuments) {
          expandedLGA[lga] = true;
          // Also expand property types with documents
          Object.keys(lgaData).forEach(pt => {
            if (lgaData[pt].length > 0) {
              expandedPT[`${lga}-${pt}`] = true;
            }
          });
        }
      });
      setExpandedLGAs(expandedLGA);
      setExpandedPropertyTypes(expandedPT);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load research documents");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLGA = (lga) => {
    setExpandedLGAs(prev => ({
      ...prev,
      [lga]: !prev[lga]
    }));
  };

  const togglePropertyType = (lga, propertyType) => {
    const key = `${lga}-${propertyType}`;
    setExpandedPropertyTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDownload = async (documentId, filename) => {
    try {
      // Use fetch API directly for binary downloads to avoid axios content-type issues
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/research/download/${documentId}`, {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download file");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getTotalDocuments = () => {
    let total = 0;
    Object.values(documents).forEach(lgaData => {
      Object.values(lgaData).forEach(docs => {
        total += docs.length;
      });
    });
    return total;
  };

  const getLGADocumentCount = (lgaData) => {
    return Object.values(lgaData).reduce((sum, docs) => sum + docs.length, 0);
  };

  const getPropertyTypeIcon = (propertyType) => {
    if (propertyType === "Houses") {
      return <Home className="w-4 h-4" />;
    }
    return <Building className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="suburb-research-page">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <MapPin className="w-8 h-8 text-[#547d91]" />
          Suburb Research
        </h2>
        <p className="text-slate-500 mt-1">
          Access research documents and reports by Local Government Area
        </p>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#547d91]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{getTotalDocuments()}</p>
              <p className="text-sm text-slate-500">Total Documents Available</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-4 leading-relaxed">
            Our data is updated monthly to ensure you have access to the most current information, supporting informed and confident purchasing decisions.
          </p>
        </CardContent>
      </Card>

      {/* LGA Folders */}
      <div className="space-y-3">
        {Object.entries(documents).map(([lga, lgaData]) => {
          const lgaDocCount = getLGADocumentCount(lgaData);
          
          return (
            <Card key={lga} className="overflow-hidden">
              {/* LGA Header */}
              <button
                onClick={() => toggleLGA(lga)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid={`lga-folder-${lga.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    lgaDocCount > 0 ? "bg-[#547d91]/10" : "bg-slate-100"
                  )}>
                    <FolderOpen className={cn(
                      "w-5 h-5",
                      lgaDocCount > 0 ? "text-[#547d91]" : "text-slate-400"
                    )} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-900">{lga}</h3>
                    <p className="text-sm text-slate-500">
                      {lgaDocCount} {lgaDocCount === 1 ? "document" : "documents"}
                    </p>
                  </div>
                </div>
                {expandedLGAs[lga] ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {/* Property Type Sub-folders */}
              {expandedLGAs[lga] && (
                <div className="border-t border-slate-100">
                  {propertyTypes.map(propertyType => {
                    const docs = lgaData[propertyType] || [];
                    const ptKey = `${lga}-${propertyType}`;
                    
                    return (
                      <div key={propertyType} className="border-b border-slate-50 last:border-b-0">
                        {/* Property Type Header */}
                        <button
                          onClick={() => togglePropertyType(lga, propertyType)}
                          className="w-full pl-8 pr-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                          data-testid={`pt-folder-${lga}-${propertyType}`.replace(/\s+/g, '-').toLowerCase()}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              docs.length > 0 ? "bg-blue-50" : "bg-slate-50"
                            )}>
                              {getPropertyTypeIcon(propertyType)}
                            </div>
                            <div className="text-left">
                              <h4 className="font-medium text-slate-800">{propertyType}</h4>
                              <p className="text-xs text-slate-500">
                                {docs.length} {docs.length === 1 ? "document" : "documents"}
                              </p>
                            </div>
                          </div>
                          {expandedPropertyTypes[ptKey] ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                        
                        {/* Documents List */}
                        {expandedPropertyTypes[ptKey] && (
                          <div className="bg-slate-50/50">
                            {docs.length === 0 ? (
                              <div className="pl-16 pr-4 py-4 text-center">
                                <p className="text-slate-400 text-sm">No documents available</p>
                              </div>
                            ) : (
                              <div className="divide-y divide-slate-100">
                                {docs.map((doc) => (
                                  <div 
                                    key={doc.document_id}
                                    className="pl-16 pr-4 py-3 flex items-center justify-between hover:bg-white transition-colors"
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-4 h-4 text-red-500" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-slate-900 truncate text-sm" title={doc.original_filename}>
                                          {doc.original_filename.replace('.pdf', '').replace('.PDF', '')}
                                        </p>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                          <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatDate(doc.uploaded_at)}
                                          </span>
                                          <span>{formatFileSize(doc.file_size)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownload(doc.document_id, doc.original_filename)}
                                      className="ml-4 flex-shrink-0"
                                      data-testid={`download-${doc.document_id}`}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
